import { Button, Select, Textarea, TextInput } from '@mantine/core';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Session } from '@/api';
import { Page } from '@/components/Ui/Page';
import { PageHeader } from '@/components/Ui/PageHeader';
import { Section, SectionBody } from '@/components/Ui/Section';
import { SUPPORT_FORM_URL } from '@/constants';
import { useAccountIdentity } from '@/hooks/useAccountIdentity';
import shared from './Account/AccountScreens.module.css';

const TOPICS = ['general', 'technical', 'bug', 'feature', 'billing'] as const;

const SUPPORT_EMAIL = 'support@formshive.com';

export interface SupportPageProps {
  session: Session | undefined;
  /** Where the form posts; without one the page shows the support address instead. */
  formSubmissionEndpoint?: string;
}

export function SupportPage({
  session,
  formSubmissionEndpoint = SUPPORT_FORM_URL,
}: SupportPageProps) {
  const { t } = useTranslation();
  const identity = useAccountIdentity();
  const accountEmail = identity.email ?? '';

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [topic, setTopic] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState<{ email?: string; topic?: string; message?: string }>({});
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!formSubmissionEndpoint) return;
    const replyTo = accountEmail || email.trim();
    const next: typeof errors = {};
    if (!/^\S+@\S+\.\S+$/.test(replyTo)) next.email = t('docs.support.emailInvalid');
    if (!topic) next.topic = t('docs.support.topicRequired');
    if (!message.trim()) next.message = t('docs.support.messageRequired');
    setErrors(next);
    if (Object.keys(next).length) return;

    setBusy(true);
    setFailed(false);
    try {
      const body = new FormData();
      body.append('name', name.trim() || identity.name || '');
      body.append('email', replyTo);
      body.append('subject', topic ?? '');
      body.append('message', message.trim());
      body.append('login_method', session?.method ?? 'unknown');
      body.append('user_identifier', session?.userId ?? 'unknown');
      // redirect=none: answer with JSON even if the support form has a thank-you page.
      const url = `${formSubmissionEndpoint}${formSubmissionEndpoint.includes('?') ? '&' : '?'}redirect=none`;
      const res = await fetch(url, {
        method: 'POST',
        body,
        headers: { Accept: 'application/json' },
        redirect: 'manual',
      });
      if (!res.ok && res.type !== 'opaqueredirect') throw new Error(`HTTP ${res.status}`);
      setSentTo(replyTo);
      setMessage('');
      setTopic(null);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Page width="narrow">
      <PageHeader title={t('docs.support.title')} subtitle={t('docs.support.subtitle')} />
      <Section title={t('docs.support.formTitle')}>
        <SectionBody>
          {!formSubmissionEndpoint ? (
            <p className={shared.help}>
              {t('docs.support.unavailable')}{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
            </p>
          ) : sentTo !== null ? (
            <div className={shared.stack} style={{ gap: 12 }} role="status">
              <span>
                {sentTo ? t('docs.support.sent', { email: sentTo }) : t('docs.support.sentNoEmail')}
              </span>
              <Button
                variant="default"
                size="xs"
                style={{ alignSelf: 'flex-start' }}
                onClick={() => setSentTo(null)}
              >
                {t('docs.support.another')}
              </Button>
            </div>
          ) : (
            <form className={shared.stack} style={{ gap: 14 }} onSubmit={submit} noValidate>
              <TextInput
                label={t('docs.support.name')}
                placeholder={identity.name ?? t('docs.support.namePlaceholder')}
                value={name}
                onChange={(e) => setName(e.currentTarget.value)}
                disabled={busy}
                maw={420}
              />
              {accountEmail ? (
                <TextInput
                  label={t('docs.support.email')}
                  description={t('docs.support.emailHelp')}
                  value={accountEmail}
                  readOnly
                  maw={420}
                />
              ) : (
                <TextInput
                  label={t('docs.support.email')}
                  placeholder={t('docs.support.emailPlaceholder')}
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.currentTarget.value)}
                  error={errors.email}
                  disabled={busy}
                  maw={420}
                />
              )}
              <Select
                label={t('docs.support.topic')}
                placeholder={t('docs.support.topicPlaceholder')}
                data={TOPICS.map((v) => ({ value: v, label: t(`docs.support.topics.${v}`) }))}
                value={topic}
                onChange={setTopic}
                error={errors.topic}
                required
                disabled={busy}
                maw={420}
              />
              <Textarea
                label={t('docs.support.message')}
                placeholder={t('docs.support.messagePlaceholder')}
                autosize
                minRows={5}
                value={message}
                onChange={(e) => setMessage(e.currentTarget.value)}
                error={errors.message}
                required
                disabled={busy}
              />
              {failed && (
                <p className={shared.errorText} role="alert">
                  {t('docs.support.failed')}
                </p>
              )}
              <div className={shared.row}>
                <Button type="submit" loading={busy}>
                  {t('docs.support.send')}
                </Button>
                <span className={shared.help}>
                  {t('docs.support.direct')} <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
                </span>
              </div>
            </form>
          )}
        </SectionBody>
      </Section>
    </Page>
  );
}
